/**
 * The full ambassador record.
 *
 * Grouped by what you would be doing when you open it: reaching them, posting
 * them something, crediting them, and seeing what they are tied to. That last
 * group is read-only on purpose — an ambassador's events are set on the event,
 * and offering to edit them here would create a second place to change one
 * relationship.
 */
import { useState } from 'react'
import { AlsoInOwnership } from '@/features/ownership/AlsoInOwnership'
import { Modal } from '@/shared/ui/Modal'
import { AMBASSADOR_STATUSES, type Person } from '@/types/events'
import type { ScoredEvent } from '@/features/events/repository/eventsRepository'
import { formatDate } from '@/shared/util/dates'
import { LinkedPosts } from '@/features/publishing/LinkedPosts'
import { AmbassadorActivity } from './AmbassadorActivity'
import { AmbassadorPremium } from './AmbassadorPremium'
import type { AmbassadorAccess } from './repository/ambassadorAccess'
import '@/shared/ui/RecordForm.css'

interface Props {
  person: Person
  /** Skida Premium from RevenueCat; absent on a new record. */
  access?: AmbassadorAccess
  linkedEvents: ScoredEvent[]
  onClose: () => void
  onSave: (patch: Partial<Person>) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

const CHANNELS: { key: keyof Person; label: string }[] = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'linkedin', label: 'LinkedIn' },
]

export function AmbassadorModal({ person, access, linkedEvents, onClose, onSave, onDelete }: Props) {
  const [form, setForm] = useState<Person>(person)
  // Two presses rather than a browser confirm(): the dialog is easy to dismiss
  // by reflex, and this is the one button in the modal that cannot be undone.
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = (patch: Partial<Person>) => setForm((f) => ({ ...f, ...patch }))

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await onSave({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        postalAddress: form.postalAddress.trim(),
        tshirt: form.tshirt.trim(),
        homeArea: form.homeArea.trim(),
        status: form.status,
        compensation: form.compensation.trim(),
        campaignCode: form.campaignCode.trim(),
        contentDelivered: form.contentDelivered.trim(),
        driveFolderUrl: form.driveFolderUrl.trim(),
        instagram: form.instagram.trim().replace(/^@/, ''),
        facebook: form.facebook.trim(),
        tiktok: form.tiktok.trim().replace(/^@/, ''),
        youtube: form.youtube.trim(),
        linkedin: form.linkedin.trim(),
        // instagramFollowers is deliberately not sent. It belongs to the
        // collector, and writing back whatever the form loaded would quietly
        // undo a fetch that landed while the modal was open.
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={person.name || 'New ambassador'}
      onClose={onClose}
      width={880}
      footer={
        <>
          {person.id && (
            <button
              className={`btn recform__delete ${confirming ? 'btn--danger' : 'btn--ghost'}`}
              disabled={saving}
              onClick={() => {
                if (!confirming) {
                  setConfirming(true)
                  return
                }
                void onDelete(person.id)
              }}
            >
              {confirming ? 'Delete for good?' : 'Delete ambassador'}
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="recform">
        <AlsoInOwnership ambassadorId={person.id} />
        <section className="recform__group">
          <h4 className="recform__legend">Reaching them</h4>
          <div className="recform__grid">
            <label className="field">
              <span>Name</span>
              <input
                className="input"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Status</span>
              <select
                className="input"
                value={form.status}
                onChange={(e) => set({ status: e.target.value as Person['status'] })}
              >
                {AMBASSADOR_STATUSES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Email</span>
              <input
                className="input"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Phone</span>
              <input
                className="input"
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Home area</span>
              <input
                className="input"
                value={form.homeArea}
                onChange={(e) => set({ homeArea: e.target.value })}
              />
            </label>
          </div>
        </section>

        <AmbassadorPremium personId={person.id} email={person.email} access={access} />

        <section className="recform__group">
          <h4 className="recform__legend">Sending them something</h4>
          <div className="recform__grid">
            <label className="field recform__wide">
              <span>Shipping address</span>
              <textarea
                className="input"
                rows={3}
                value={form.postalAddress}
                onChange={(e) => set({ postalAddress: e.target.value })}
                placeholder="Street, postcode, city, country"
              />
            </label>
            <label className="field">
              <span>T-shirt size</span>
              <input
                className="input"
                value={form.tshirt}
                onChange={(e) => set({ tshirt: e.target.value })}
                placeholder="S / M / L / XL"
              />
            </label>
          </div>
        </section>

        <section className="recform__group">
          <h4 className="recform__legend">Crediting them</h4>
          <div className="recform__grid">
            {/* Handle and follower count sit together: one is the key and the
                other is what the key buys, and reading them apart made the
                count look like a free-standing opinion. */}
            <label className="field">
              <span>Instagram</span>
              <input
                className="input"
                value={form.instagram}
                onChange={(e) => set({ instagram: e.target.value })}
                placeholder="without the @"
              />
            </label>
            <label className="field">
              <span>Instagram followers</span>
              <input
                className="input"
                value={
                  form.instagramFollowers !== null
                    ? form.instagramFollowers.toLocaleString('nb-NO')
                    : form.igAccountType === 'personal'
                      ? 'personal account — unavailable'
                      : 'not fetched yet'
                }
                readOnly
                disabled
                title={
                  form.igAccountType === 'personal'
                    ? 'Instagram answers follower counts for professional accounts only. This one is personal, so there is no number to fetch — not a failure, and not something waiting.'
                    : 'Read-only: this is fetched, not typed.'
                }
              />
            </label>

            {CHANNELS.filter((c) => c.key !== 'instagram').map((c) => (
              <label className="field" key={c.key}>
                <span>{c.label}</span>
                <input
                  className="input"
                  value={String(form[c.key] ?? '')}
                  onChange={(e) => set({ [c.key]: e.target.value } as Partial<Person>)}
                />
              </label>
            ))}

            {/* Whether their profile points back at us — the thing a
                partnership is for, and previously answerable only by opening
                Instagram and reading. */}
            {form.instagramFetchedAt && (
              <div className="ambact__profile">
                <span
                  className={`pill ${
                    form.instagramMentionsSkida ? 'ambact__mentions' : 'ambact__nomentions'
                  }`}
                >
                  {form.instagramMentionsSkida
                    ? 'Profile mentions Skida'
                    : 'No mention of us in their profile'}
                </span>
                {form.instagramBio && <p className="ambact__bio">{form.instagramBio}</p>}
              </div>
            )}

            <AmbassadorActivity handle={form.instagram} />
          </div>
          <p className="recform__note">
            The follower count is locked because a hand-typed number goes stale the day after it
            is typed, and nothing in the interface would ever say so. It is filled nightly for
            ambassadors on a professional account. On a personal account there is nothing to
            fetch — Instagram does not answer for them, and we do not read profiles any other
            way — so the field says so rather than sitting empty as if it were waiting.
          </p>
        </section>

        <section className="recform__group">
          <h4 className="recform__legend">The deal</h4>
          <div className="recform__grid">
            <label className="field">
              <span>Compensation</span>
              <input
                className="input"
                value={form.compensation}
                onChange={(e) => set({ compensation: e.target.value })}
                placeholder="Product, fee, affiliate…"
              />
            </label>
            <label className="field">
              <span>Campaign code</span>
              <input
                className="input"
                value={form.campaignCode}
                onChange={(e) => set({ campaignCode: e.target.value })}
              />
            </label>
            <label className="field recform__wide">
              <span>Content delivered</span>
              <textarea
                className="input"
                rows={2}
                value={form.contentDelivered}
                onChange={(e) => set({ contentDelivered: e.target.value })}
              />
            </label>
            {/*
              Their folder in the shared Medialibrary drive. Created by hand and
              pasted here — Drive's own sharing is what grants access, so this
              field is a pointer and not a permission. Which is why the link is
              worth opening from here before saving it: a folder shared with
              nobody looks identical to one shared correctly until somebody
              outside the company tries it.
            */}
            <label className="field recform__wide">
              <span>Upload folder</span>
              <input
                className="input"
                value={form.driveFolderUrl}
                onChange={(e) => set({ driveFolderUrl: e.target.value })}
                placeholder="https://drive.google.com/drive/folders/…"
              />
              <span className="field__note">
                Their own folder under Medialibrary, for photos and footage. Create it in{' '}
                <a
                  href="https://drive.google.com/drive/folders/1BHL9P_FIkOFI9aBGQE4g8lZX0tgp1SXP"
                  target="_blank"
                  rel="noreferrer"
                >
                  Medialibrary
                </a>
                , share it with them as an editor, then paste the link.
                {form.driveFolderUrl.trim() && (
                  <>
                    {' '}
                    <a href={form.driveFolderUrl.trim()} target="_blank" rel="noreferrer">
                      Open this one
                    </a>
                    .
                  </>
                )}
              </span>
            </label>
          </div>
        </section>

        <section className="recform__group">
          <h4 className="recform__legend">Linked events</h4>
          {linkedEvents.length === 0 ? (
            <p className="recform__note">
              Not assigned to any event yet. Assignments are made on the event, so this list
              follows from there rather than being edited here.
            </p>
          ) : (
            <ul className="recform__links">
              {linkedEvents.map((e) => (
                <li key={e.id}>
                  <strong>{e.name}</strong>
                  <span className="muted">
                    {' '}
                    {e.startDate ? formatDate(e.startDate) : e.dateConfidence || 'no date'} ·{' '}
                    {e.country}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* Renders nothing when this ambassador is not credited on any post,
              so the section stays about events until there is a reason not to. */}
          <LinkedPosts ambassadorId={person.id} />
        </section>

        {error && <div className="recform__error">{error}</div>}

      </div>
    </Modal>
  )
}
